import { useEffect, useRef } from 'react';

/**
 * Tasteful scroll reveal.
 *
 * Attach the returned ref to an element that already has the `.reveal` class.
 * When the element scrolls into view it gets the `in-view` class once and the
 * observer disconnects. Only `opacity` + `transform` are animated (see
 * index.css) so this never triggers layout. The `prefers-reduced-motion`
 * block in index.css forces `.reveal` visible, and we also reveal immediately
 * here when the API is unavailable.
 *
 * @param {{ threshold?: number, rootMargin?: string }} [options]
 */
export function useReveal(options = {}) {
  const ref = useRef(null);
  const { threshold = 0.15, rootMargin = '0px 0px -10% 0px' } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reveal = () => el.classList.add('in-view');

    // Already revealed (e.g. re-run) — nothing to do.
    if (el.classList.contains('in-view')) return undefined;

    // Above-the-fold / already in (or near) the viewport on mount: reveal
    // immediately so on-load content can never be stuck invisible. We still
    // get the CSS transition because the class is added in an effect, after
    // the initial paint of the `.reveal` (opacity:0) state.
    if (typeof window !== 'undefined' && typeof el.getBoundingClientRect === 'function') {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        reveal();
        return undefined;
      }
    }

    // No IntersectionObserver (or SSR) — just show it.
    if (typeof IntersectionObserver === 'undefined') {
      reveal();
      return undefined;
    }

    // Final safety net: even if the observer never fires, nothing stays hidden.
    const failsafe = setTimeout(reveal, 1200);

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            obs.unobserve(entry.target);
            clearTimeout(failsafe);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => {
      clearTimeout(failsafe);
      observer.disconnect();
    };
  }, [threshold, rootMargin]);

  return ref;
}
